import React, { useEffect, useMemo } from "react";
import { Paper, Tab, Tabs } from "@material-ui/core";

import { BusinessTable } from "components/Business/BusinessTable/BusinessesList";
import { BusinessRelationsTable } from "components/Business/BusinessRelations/Table";
import { SideLoadedBusinessManageTable } from "components/Business/SideLoadedBusinesses/SideLoadedBusinessManageTable";
import { Link, Route, Switch, useRouteMatch } from "react-router-dom";
import { useGetSelectedTabFromRoute } from "hooks/useGetSelectedTabFromRoute";
import history from "appHistory";
import { getManageBusinessRoute } from "lib/navigation";
import {
  useHasDeploymentPermission,
  useHasSaleyardOrDeploymentPermission,
} from "hooks";
import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";

interface ManageBusinessesTab {
  label: string;
  path?: string;
  component: React.ComponentType;
}

const getTabPath = ({ label, path }: ManageBusinessesTab) => path || label;

export function ManageBusinesses(): React.JSX.Element {
  const match = useRouteMatch();

  const hasRelationshipPermissions = useHasDeploymentPermission(
    DeploymentPermission.featureBusinessRelations,
  );

  const hasSideLoadedDataManagementPermission =
    useHasSaleyardOrDeploymentPermission(
      DeploymentPermission.featureSideLoadedDataManagement,
      SaleyardPermission.featureSideLoadedDataManagement,
    );

  // Records only sync under the data sets feature, so the tab would be empty
  // without it.
  const hasSideLoadedDataSetsPermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureSideLoadedDataSets,
    SaleyardPermission.featureSideLoadedDataSets,
  );

  const canManageSideLoadedData =
    hasSideLoadedDataManagementPermission && hasSideLoadedDataSetsPermission;

  const tabs: ManageBusinessesTab[] = useMemo(
    () =>
      [
        {
          label: "Businesses",
          component: BusinessTable,
        },
        hasRelationshipPermissions && {
          label: "Relationships",
          component: BusinessRelationsTable,
        },
        canManageSideLoadedData && {
          label: "Legacy Data",
          path: "LegacyData",
          component: SideLoadedBusinessManageTable,
        },
      ].filter(Boolean),
    [hasRelationshipPermissions, canManageSideLoadedData],
  );

  const currentTab = useGetSelectedTabFromRoute(tabs);
  useEffect(() => {
    // If we aren't on a tab, open the first.
    if (!currentTab && tabs[0]?.label) {
      history.push(getManageBusinessRoute(getTabPath(tabs[0])));
    }
  }, [currentTab, match.path, tabs]);

  const routeMap = tab => (
    <Route
      path={`${match.path}${getTabPath(tab)}/`}
      component={tab.component}
      key={tab.label}
    />
  );
  const tabMap = tab => (
    <Tab
      label={tab.label}
      key={`tab-${tab.label}`}
      value={getTabPath(tab)}
      data-tour={getTabPath(tab)}
      component={Link}
      to={`${match.path}${getTabPath(tab)}/`}
    />
  );

  return (
    <>
      <Paper>
        <Tabs value={currentTab} variant="scrollable">
          {tabs.map(tabMap)}
        </Tabs>
      </Paper>

      <Switch>{tabs.map(routeMap)}</Switch>
    </>
  );
}
